import { cn } from "@/modern/utils";
import { Badge } from "@/modern/components/ui/badge";
import { Card, CardContent } from "@/modern/components/ui/card-shadcn";
import { kpiContextBadgeClass } from "../constants";
import { useCoverage } from "../context/CoverageContext";

export const CoverageKpiCards = () => {
  const { kpiData } = useCoverage();

  const noData = kpiData.hubsTotal === 0;

  const readyPercent = kpiData.hubsTotal > 0
    ? Math.round((kpiData.outputReadyCount / kpiData.hubsTotal) * 100)
    : 0;
  const emptyCount = kpiData.requiredTotal - kpiData.requiredFilled;

  return (
    <div className="grid grid-cols-4 gap-3 mb-5">
      {/* Output ready */}
      <Card className="rounded-xl">
        <CardContent className="px-4 py-3.5">
          <p className="text-sm font-medium text-muted-foreground mb-1.5">Output ready</p>
          <div className="flex items-baseline gap-1">
            <span className={cn("text-3xl font-semibold tracking-tight leading-none", noData ? "text-muted-foreground/40" : "text-success")}>
              {noData ? "—" : kpiData.outputReadyCount}
            </span>
            {!noData && <span className="text-sm text-muted-foreground/60">/{kpiData.hubsTotal}</span>}
          </div>
          <p className="text-xs text-muted-foreground mt-1">{noData ? "No hubs to measure" : "Ready to publish"}</p>
          {!noData && (
            <Badge variant="secondary" size="sm" className={kpiContextBadgeClass("landscape_ready", "mt-2.5")}>
              {readyPercent}% of landscape
            </Badge>
          )}
        </CardContent>
      </Card>

      {/* Content roles filled */}
      <Card className="rounded-xl">
        <CardContent className="px-4 py-3.5">
          <p className="text-sm font-medium text-muted-foreground mb-1.5">Content roles filled</p>
          <span className={cn("text-3xl font-semibold tracking-tight leading-none", noData ? "text-muted-foreground/40" : "text-amber-500")}>
            {noData ? "—" : `${kpiData.requiredPercent}%`}
          </span>
          <p className="text-xs text-muted-foreground mt-1">
            {noData ? "No roles configured" : `${kpiData.requiredFilled} of ${kpiData.requiredTotal} required`}
          </p>
          {!noData && (
            <>
              <div className="h-0.5 bg-muted/30 rounded-full mt-2.5">
                <div
                  className="h-full rounded-full bg-amber-500 transition-[width] duration-300"
                  style={{ width: `${Math.min(kpiData.requiredPercent, 100)}%` }}
                />
              </div>
              {emptyCount > 0 && (
                <p className="text-2xs text-muted-foreground/60 mt-1.5">
                  {emptyCount} still empty
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Sequence capable */}
      <Card className="rounded-xl">
        <CardContent className="px-4 py-3.5">
          <p className="text-sm font-medium text-muted-foreground mb-1.5">Sequence capable</p>
          <span className={cn("text-3xl font-semibold tracking-tight leading-none", noData ? "text-muted-foreground/40" : "text-primary")}>
            {noData ? "—" : kpiData.sequenceReadyCount}
          </span>
          <p className="text-xs text-muted-foreground mt-1">{noData ? "No temporal data" : "Valid before & after"}</p>
          {!noData && (
            <Badge variant="secondary" size="sm" className={kpiContextBadgeClass("sequence_capable", "mt-2.5")}>
              Before / After available
            </Badge>
          )}
        </CardContent>
      </Card>

      {/* Blind spots */}
      <Card className="rounded-xl">
        <CardContent className="px-4 py-3.5">
          <p className="text-sm font-medium text-muted-foreground mb-1.5">Blind spots</p>
          <span className={cn("text-3xl font-semibold tracking-tight leading-none", noData ? "text-muted-foreground/40" : kpiData.blindSpotCount > 0 ? "text-destructive" : "text-success")}>
            {noData ? "—" : kpiData.blindSpotCount}
          </span>
          <p className="text-xs text-muted-foreground mt-1">{noData ? "Nothing to track" : "Zero-asset hubs"}</p>
          {!noData && (
            kpiData.blindSpotCount > 0 ? (
              <Badge variant="secondary" size="sm" className={kpiContextBadgeClass("blind_spot_attention", "mt-2.5")}>
                Needs attention
              </Badge>
            ) : (
              <Badge variant="secondary" size="sm" className={kpiContextBadgeClass("blind_spot_clear", "mt-2.5")}>
                All clear
              </Badge>
            )
          )}
        </CardContent>
      </Card>
    </div>
  );
};
